import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  AlertCircle,
  FileText,
  Clock,
  CheckCircle,
  Edit2,
  Save,
  X,
  Loader2,
  Tag,
  Hash,
} from "lucide-react";
import { knowledgeAPI } from "../api";
import { useAuthStore } from "../store/authStore";
import toast from "react-hot-toast";
import clsx from "clsx";

const KnowledgeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  const [isEditing, setIsEditing] = useState(false);
  const [editedSections, setEditedSections] = useState([]);
  const [editedMetadata, setEditedMetadata] = useState({
    category: "",
    tags: [],
  });
  const [newTagInput, setNewTagInput] = useState("");

  const {
    data: document,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["knowledge-document", id],
    queryFn: () => knowledgeAPI.getById(id).then((res) => res.data.data),
  });

  const isAdmin = user?.role === "super_admin" || user?.role === "admin";

  useEffect(() => {
    if (document) {
      setEditedSections(document.content?.sections || []);
      setEditedMetadata({
        category: document.category || "",
        tags: document.tags || [],
      });
    }
  }, [document]);

  const saveMutation = useMutation({
    mutationFn: (data) => knowledgeAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["knowledge-document", id] });
      toast.success("Changes saved as draft");
      // Keep editing mode active until explicitly cancelled or approved
    },
    onError: (err) =>
      toast.error(err.response?.data?.error || "Failed to save changes"),
  });

  const approveMutation = useMutation({
    mutationFn: (status) => knowledgeAPI.updateStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["knowledge-document", id] });
      toast.success("Document approved and published");
      setIsEditing(false);
    },
    onError: () => toast.error("Failed to update status"),
  });

  if (isLoading)
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="animate-spin text-blue-600 w-10 h-10" />
      </div>
    );
  if (error)
    return (
      <div className="p-12 text-center text-red-500 flex flex-col items-center">
        <AlertCircle className="w-8 h-8 mb-4" />
        <p className="text-xl font-medium">Error loading document</p>
      </div>
    );

  const handleSectionChange = (index, field, value) => {
    const updated = [...editedSections];
    updated[index][field] = value;
    setEditedSections(updated);
  };

  const onSaveDraft = () => {
    saveMutation.mutate({
      content: { sections: editedSections },
      category: editedMetadata.category,
      tags: editedMetadata.tags,
    });
  };

  const onApproveAndPublish = () => {
    // Save current changes and then trigger approval
    saveMutation.mutate(
      {
        content: { sections: editedSections },
        category: editedMetadata.category,
        tags: editedMetadata.tags,
      },
      {
        onSuccess: () => approveMutation.mutate("approved"),
      },
    );
  };

  const handleTagAdd = (e) => {
    if (e.key === "Enter" && newTagInput.trim()) {
      const newTag = newTagInput.trim().toLowerCase();
      if (!editedMetadata.tags.includes(newTag)) {
        setEditedMetadata((prev) => ({
          ...prev,
          tags: [...prev.tags, newTag],
        }));
      }
      setNewTagInput("");
    }
  };

  const handleTagRemove = (tagToRemove) => {
    setEditedMetadata((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const Button = ({
    children,
    variant = "primary",
    className = "",
    ...props
  }) => {
    let baseClasses =
      "btn px-4 py-2 rounded-xl font-semibold transition-colors duration-200 flex items-center justify-center text-sm";
    if (variant === "primary")
      baseClasses += " bg-blue-600 text-white hover:bg-blue-700 shadow-md";
    if (variant === "secondary")
      baseClasses += " bg-slate-100 text-slate-700 hover:bg-slate-200";
    if (variant === "ghost")
      baseClasses += " bg-transparent text-slate-500 hover:bg-slate-100";
    if (props.disabled) baseClasses += " opacity-60 cursor-not-allowed";

    return (
      <button className={clsx(baseClasses, className)} {...props}>
        {children}
      </button>
    );
  };

  const Card = ({ children, className = "" }) => (
    <div
      className={clsx(
        "bg-white border border-slate-200 p-6 rounded-2xl shadow-xl",
        className,
      )}
    >
      {children}
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-6 border-b border-slate-200 pb-4">
        <div className="flex items-center gap-4">
          <Link to="/knowledge">
            <Button variant="ghost" className="p-3">
              <ArrowLeft className="w-6 h-6" />
            </Button>
          </Link>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-900">
              {document.title}
            </h1>
            <p className="text-md text-slate-500 mt-1">
              {document.documentType} • Version {document.version || "1.0"}
            </p>
          </div>
        </div>

        {isAdmin && !isEditing && document.status !== "approved" && (
          <Button
            onClick={() => setIsEditing(true)}
            variant="secondary"
            className="gap-2 mt-2"
          >
            <Edit2 className="w-4 h-4" /> Start Review
          </Button>
        )}
      </div>

      {/* Status/Action Bar */}
      {isAdmin && document.status !== "approved" && (
        <div
          className={clsx(
            "p-5 rounded-xl border flex items-center justify-between shadow-md",
            isEditing
              ? "bg-blue-50 border-blue-300"
              : "bg-yellow-50 border-yellow-300",
          )}
        >
          <div className="flex items-center gap-4">
            {isEditing ? (
              <Edit2 className="w-6 h-6 text-blue-600 flex-shrink-0" />
            ) : (
              <Clock className="w-6 h-6 text-yellow-600 flex-shrink-0" />
            )}

            <div>
              <p className="text-lg text-slate-800 font-bold">
                {isEditing ? "Editing & Reviewing Draft" : "Pending Approval"}
              </p>
              <p className="text-sm text-slate-600">
                Ensure all AI-extracted information is accurate and complete
                before publishing.
              </p>
            </div>
          </div>
          <div className="flex gap-3 flex-shrink-0">
            {isEditing ? (
              <>
                <Button
                  onClick={() => setIsEditing(false)}
                  variant="ghost"
                  className="text-slate-500"
                >
                  <X className="w-4 h-4" /> Cancel
                </Button>
                <Button
                  onClick={onSaveDraft}
                  variant="secondary"
                  disabled={saveMutation.isPending}
                >
                  {saveMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Save Draft
                </Button>
                <Button
                  onClick={onApproveAndPublish}
                  variant="primary"
                  disabled={approveMutation.isPending || saveMutation.isPending}
                >
                  <CheckCircle className="w-4 h-4" /> Approve & Publish
                </Button>
              </>
            ) : (
              <Button onClick={() => setIsEditing(true)} variant="primary">
                Start Review
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Main Content and Sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content (Sections) */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <h3 className="text-2xl font-bold text-slate-800 mb-6 border-b border-slate-100 pb-3">
              Document Content
            </h3>
            <div className="space-y-8">
              {(isEditing ? editedSections : document.content?.sections)?.map(
                (section, idx) => (
                  <div
                    key={idx}
                    className="p-4 rounded-xl border border-slate-200 bg-slate-50"
                  >
                    {isEditing ? (
                      <div className="space-y-4">
                        <input
                          className="w-full p-2 border border-slate-300 rounded-lg font-bold text-lg text-blue-700 focus:ring-blue-500 focus:border-blue-500"
                          value={section.title}
                          onChange={(e) =>
                            handleSectionChange(idx, "title", e.target.value)
                          }
                        />
                        <textarea
                          className="w-full p-2 border border-slate-300 rounded-lg text-sm min-h-[120px] focus:ring-blue-500 focus:border-blue-500 resize-none"
                          value={section.content}
                          onChange={(e) =>
                            handleSectionChange(idx, "content", e.target.value)
                          }
                        />
                      </div>
                    ) : (
                      <>
                        <h4 className="text-xl font-bold text-blue-600 mb-3">
                          {idx + 1}. {section.title}
                        </h4>
                        <p className="text-slate-700 text-base leading-relaxed whitespace-pre-wrap">
                          {section.content}
                        </p>
                      </>
                    )}
                  </div>
                ),
              )}
            </div>
          </Card>
        </div>

        {/* Sidebar (Metadata) */}
        <div className="space-y-8">
          <Card>
            <h3 className="text-2xl font-bold text-slate-800 mb-6 border-b border-slate-100 pb-3">
              Metadata
            </h3>
            <div className="space-y-6 text-sm">
              <div className="flex flex-col gap-2">
                <span className="text-slate-500 font-semibold flex items-center gap-2">
                  <Hash className="w-4 h-4" /> Category
                </span>
                {isEditing ? (
                  <input
                    className="w-full p-2 border border-slate-300 rounded-lg text-sm font-medium"
                    value={editedMetadata.category}
                    onChange={(e) =>
                      setEditedMetadata({
                        ...editedMetadata,
                        category: e.target.value,
                      })
                    }
                    placeholder="e.g., Safety, Maintenance, Operations"
                  />
                ) : (
                  <span className="px-3 py-1 bg-slate-100 text-slate-800 rounded-full font-medium w-fit">
                    {document.category || "Uncategorized"}
                  </span>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <span className="text-slate-500 font-semibold flex items-center gap-2">
                  <Tag className="w-4 h-4" /> Tags
                </span>
                <div className="flex flex-wrap gap-2">
                  {(isEditing ? editedMetadata.tags : document.tags)?.map(
                    (tag, i) => (
                      <span
                        key={i}
                        className="px-3 py-1 text-xs bg-blue-100 text-blue-800 rounded-full font-medium flex items-center gap-1"
                      >
                        {tag}
                        {isEditing && (
                          <button
                            onClick={() => handleTagRemove(tag)}
                            className="ml-1 text-blue-500 hover:text-blue-700"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </span>
                    ),
                  )}
                  {isEditing && (
                    <input
                      type="text"
                      value={newTagInput}
                      onChange={(e) => setNewTagInput(e.target.value)}
                      onKeyDown={handleTagAdd}
                      placeholder="Add tag (Enter)"
                      className="p-1.5 border border-slate-300 rounded-lg text-xs w-28"
                    />
                  )}
                </div>
              </div>

              <div className="pt-6 border-t border-slate-100 space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-slate-500 font-semibold">Status</span>
                  <span
                    className={clsx(
                      "px-3 py-1 text-xs font-bold rounded-full w-fit",
                      document.status === "approved"
                        ? "bg-green-100 text-green-700"
                        : "bg-yellow-100 text-yellow-700",
                    )}
                  >
                    {document.status?.toUpperCase().replace("_", " ")}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-500 font-semibold">
                    AI Confidence
                  </span>
                  <span
                    className={clsx(
                      "font-bold text-lg",
                      (document.extraction?.confidenceScore || 0) > 0.8
                        ? "text-green-600"
                        : "text-yellow-600",
                    )}
                  >
                    {Math.round(
                      (document.extraction?.confidenceScore || 0) * 100,
                    )}
                    %
                  </span>
                </div>
              </div>
            </div>
          </Card>

          {/* <Card>
            <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2 text-xl">
              <FileText className="w-5 h-5 text-slate-500" /> Original Source
            </h3>
            <p className="text-sm text-slate-500 mb-5">
              Extracted from:{" "}
              <span className="font-medium text-slate-700">
                {document.originalName || "uploaded file"}
              </span>
            </p>
            <div className="text-sm text-blue-600 hover:text-blue-700 cursor-pointer font-semibold flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <FileText className="w-4 h-4" />
              <span>Download Raw Document</span>
            </div>
          </Card> */}
        </div>
      </div>
    </div>
  );
};

export default KnowledgeDetail;
